import React from "react";
import { Send } from "lucide-react";

/**
 * The footer that appears once clients are ticked in the dock's select mode.
 *
 * Visually the same object as `Support/BulkActionBar` — a white, shadowed bar
 * pinned to the bottom of the list with the count on the left and the action on
 * the right — so the two multi-select surfaces in this app read as one idea.
 *
 * Renders nothing at zero selected: an empty action bar is just a strip of
 * chrome eating list height.
 *
 * @param {Object} props
 * @param {number} props.selectedCount
 * @param {number} props.maxRecipients - Server cap; over it, sending is blocked.
 * @param {() => void} props.onClear
 * @param {() => void} props.onCompose
 */
export function BulkSelectionBar({
  selectedCount,
  maxRecipients,
  onClear,
  onCompose,
}) {
  if (selectedCount === 0) return null;

  const overLimit = selectedCount > maxRecipients;

  return (
    <div className="flex flex-shrink-0 items-center gap-2 border-t border-gray-100 bg-white px-3 py-2.5 shadow-[0_-2px_8px_-4px_rgba(15,23,42,0.15)]">
      <span className="text-xs font-semibold text-gray-800">
        {selectedCount} selected
      </span>

      {/* Refuse rather than silently truncating to the first N — a send that
          quietly skipped 40 people would look like it worked. */}
      {overLimit && (
        <span className="text-xs font-semibold text-danger-dark">
          Max {maxRecipients}
        </span>
      )}

      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
      >
        Clear
      </button>
      <button
        type="button"
        onClick={onCompose}
        disabled={overLimit}
        className="inline-flex items-center gap-1.5 rounded-lg bg-orange-primary px-3 py-1.5 text-xs font-semibold text-white transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Send className="h-3.5 w-3.5" aria-hidden="true" />
        Message {selectedCount}
      </button>
    </div>
  );
}

export default BulkSelectionBar;
